import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../axiosInstance';
import { changeLanguage as i18nChangeLanguage, SUPPORTED_LANGUAGES } from '../i18n';

const RTL_LANGUAGES = new Set(SUPPORTED_LANGUAGES.filter(l => l.dir === 'rtl').map(l => l.code));

const applyDirection = (langCode) => {
  const dir = RTL_LANGUAGES.has(langCode) ? 'rtl' : 'ltr';
  document.documentElement.dir = dir;
  document.documentElement.lang = langCode;
};

const LanguageContext = createContext({
  language: 'en',
  setLanguage: () => {},
  languages: SUPPORTED_LANGUAGES,
  isLoading: false,
  isRTL: false,
});

export const useLanguage = () => useContext(LanguageContext);

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [language, setLanguageState] = useState(i18n.language || 'en');
  const [isLoading, setIsLoading] = useState(false);
  const isRTL = RTL_LANGUAGES.has(language);

  // Apply direction on mount
  useEffect(() => {
    applyDirection(language);
  }, [language]);

  // Sync language from API on mount (for authenticated users)
  useEffect(() => {
    const syncFromAPI = async () => {
      try {
        const response = await axiosInstance.get('/user/display');
        if (response.data.success && response.data.data?.language) {
          const apiLang = response.data.data.language;
          if (apiLang !== language && SUPPORTED_LANGUAGES.some(l => l.code === apiLang)) {
            setIsLoading(true);
            await i18nChangeLanguage(apiLang);
            setLanguageState(apiLang);
            setIsLoading(false);
          }
        }
      } catch {
        // Not authenticated or API error — use detected language
      }
    };
    syncFromAPI();
  }, []);

  // Change language — updates i18next + localStorage + API
  const setLanguage = useCallback(async (langCode) => {
    if (langCode === language) return;
    setIsLoading(true);
    try {
      await i18nChangeLanguage(langCode);
      setLanguageState(langCode);

      // Persist to API (fire and forget)
      axiosInstance.post('/user/display/update', { language: langCode }).catch(() => {});
    } catch (error) {
      console.error('Failed to change language:', error);
    } finally {
      setIsLoading(false);
    }
  }, [language]);

  // Listen for i18n language changes from other sources
  useEffect(() => {
    const handleLanguageChanged = (lng) => {
      setLanguageState(lng);
    };
    i18n.on('languageChanged', handleLanguageChanged);
    return () => i18n.off('languageChanged', handleLanguageChanged);
  }, [i18n]);

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        languages: SUPPORTED_LANGUAGES,
        isLoading,
        isRTL,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
